const testApiUrl = (import.meta.env.VITE_TEST_API_URL || '/test-api').replace(/\/$/, '');

export type TestSession = {
  user: { objectId: string; username?: string; email?: string };
  role: string;
  appName: string;
  buildConfigId: string;
};

export type TestRecord = {
  objectId: string;
  TestTitle?: string;
  TestDesc?: string;
  Type?: string;
  ImgIcon?: string;
  ImgBanner?: string;
  CourseTitle?: string;
  CoursePtr?: { objectId?: string; CourseTitle?: string } | string;
  ParentFolder?: { objectId?: string; TestTitle?: string } | string;
  NoOfQuestions?: number;
  TotalMarks?: number;
  PositiveMarks?: number;
  NegativeMarks?: number;
  TestFromDate?: string | { iso?: string };
  TestToDate?: string | { iso?: string };
  Time?: number;
  EvaluationMode?: boolean;
  EvalutionMode?: boolean;
  PracticeMode?: boolean;
  ImmediateTestResult?: boolean;
  TestPaidType?: string;
  Sections?: string[];
  Status?: string;
  IsPublished?: boolean;
  IsDeleted?: boolean;
  DisplayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type QuestionRecord = {
  objectId: string;
  QuesTitle?: string;
  Opt1?: string;
  Opt2?: string;
  Opt3?: string;
  Opt4?: string;
  Opt5?: string;
  CorrectAnswer?: string[];
  SolutionInfo?: string;
  Type?: string;
  Marks?: number;
  NegativeMarks?: number;
  CourseTitle?: string;
  SubjectName?: string;
  CoursePtr?: { objectId?: string } | string;
  SubjectsPtr?: { objectId?: string } | string;
  ParentFolder?: { objectId?: string; QuesTitle?: string } | string;
  createdAt?: string;
  updatedAt?: string;
};

export type TestMapping = {
  objectId: string;
  indexNo?: number;
  positiveMarks?: number;
  negativeMarks?: number;
  sectionName?: string;
  question?: QuestionRecord;
};

export type ResultRecord = {
  objectId: string;
  CustName?: string;
  CourseTitle?: string;
  TestTitle?: string;
  ObtainedMarks?: number;
  TotalMarks?: number;
  Percentage?: number;
  YourGrade?: string;
  Status?: string;
  TestDate?: string | { iso?: string };
  createdAt?: string;
};

export type ReportedQuestion = {
  objectId: string;
  Comment?: string;
  CustomerPtr?: { objectId?: string } | string;
  TestInfoPtr?: { objectId?: string } | string;
  TestQuestionsPtr?: { objectId?: string } | string;
  studentName?: string;
  customerName?: string;
  testTitle?: string;
  questionTitle?: string;
  courseTitle?: string;
  subjectName?: string;
  createdAt?: string;
};

export type QuestionOfDayRecord = { objectId: string; Date?: string | { iso?: string }; QuestionPtr?: { objectId?: string } | string; questionTitle?: string; courseTitle?: string; subjectName?: string };

export type Paginated<T> = { total: number; skip: number; limit: number; hasMore: boolean; results: T[] };

async function request<T>(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  const response = await fetch(`${testApiUrl}${path}`, { ...init, headers, credentials: 'include' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const failure = new Error(body.error || `Test console request failed (${response.status})`);
    Object.assign(failure, { status: response.status, rowErrors: body.rowErrors });
    throw failure;
  }
  return body as T;
}

export const testApi = {
  async login(username: string, password: string) {
    return request<TestSession>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
  },
  async logout() {
    return request<Record<string, never>>('/auth/logout', { method: 'POST' });
  },
  async session() {
    return request<TestSession>('/session');
  },
  async config() {
    return request<{ appName: string; scope: string; buildConfigId: string; allowedRoles: string[] }>('/config');
  },
  async health() {
    return request<{ service: string; status: string; scope: string; buildConfigId: string; version: string }>('/health');
  },
  async uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${testApiUrl}/files`, { method: 'POST', body: formData, credentials: 'include', headers: { Accept: 'application/json' } });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `Test media upload failed (${response.status})`);
    return body as { status: string; imageUrl: string; name: string; size: number };
  },
  async dashboard() {
    return request<{ stats: { tests: number; folders: number; questions: number; results: number; pendingResults: number; published: number }; recentTests: TestRecord[]; recentQuestions: QuestionRecord[] }>('/dashboard');
  },
  async listTests(options: { search?: string; kind?: string; courseId?: string; parentFolderId?: string; order?: string; skip?: number; limit?: number } = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)); });
    return request<Paginated<TestRecord>>(`/tests?${params.toString()}`);
  },
  async getTest(objectId: string) {
    return request<TestRecord>(`/tests/${encodeURIComponent(objectId)}`);
  },
  async saveTest(data: Record<string, unknown>, objectId?: string) {
    return request<TestRecord>(objectId ? `/tests/${encodeURIComponent(objectId)}` : '/tests', { method: objectId ? 'PUT' : 'POST', body: JSON.stringify(data) });
  },
  async publishTest(objectId: string, published: boolean) {
    return request<{ objectId: string; isPublished: boolean }>(`/tests/${encodeURIComponent(objectId)}/publish`, { method: 'POST', body: JSON.stringify({ published }) });
  },
  async moveFolder(objectId: string, direction: 'up' | 'down') {
    return request<{ objectId: string; moved: boolean; displayOrder?: number }>(`/tests/${encodeURIComponent(objectId)}/move`, { method: 'POST', body: JSON.stringify({ direction }) });
  },
  async duplicateTest(objectId: string, destination: { courseId: string; folderId?: string }) {
    return request<{ message: string; test: TestRecord }>(`/tests/${encodeURIComponent(objectId)}/duplicate`, { method: 'POST', body: JSON.stringify(destination) });
  },
  async archiveTest(objectId: string) {
    return request<{ objectId: string; deleted: boolean }>(`/tests/${encodeURIComponent(objectId)}`, { method: 'DELETE' });
  },
  async listCourses() {
    return request<Paginated<{ objectId: string; CourseTitle?: string; CourseSubTitle?: string }>>('/courses?limit=100&order=-updatedAt');
  },
  async listSubjects(courseId?: string) {
    const params = new URLSearchParams({ limit: '100', order: '-updatedAt' });
    if (courseId) params.set('courseId', courseId);
    return request<Paginated<{ objectId: string; SubjectName?: string; CoursePtr?: { objectId?: string } | string }>>(`/subjects?${params.toString()}`);
  },
  async listQuestions(options: { search?: string; type?: string; courseId?: string; subjectId?: string; folderId?: string; order?: string; skip?: number; limit?: number } = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)); });
    return request<Paginated<QuestionRecord>>(`/questions?${params.toString()}`);
  },
  async getQuestion(objectId: string) {
    return request<QuestionRecord>(`/questions/${encodeURIComponent(objectId)}`);
  },
  async saveQuestion(data: Record<string, unknown>, objectId?: string) {
    return request<QuestionRecord>(objectId ? `/questions/${encodeURIComponent(objectId)}` : '/questions', { method: objectId ? 'PUT' : 'POST', body: JSON.stringify(data) });
  },
  async archiveQuestion(objectId: string) {
    return request<{ objectId: string; deleted: boolean }>(`/questions/${encodeURIComponent(objectId)}`, { method: 'DELETE' });
  },
  async bulkQuestionAction(action: 'archive' | 'add-to-test' | 'add-to-qod', questionIds: string[], options: { testId?: string; date?: string } = {}) {
    return request<{ action: string; count: number; skipped?: number }>('/questions/bulk', { method: 'POST', body: JSON.stringify({ action, questionIds, ...options }) });
  },
  async importQuestions(rows: Array<Record<string, unknown>>) {
    return request<{ action: string; count: number }>('/questions/import', { method: 'POST', body: JSON.stringify({ rows }) });
  },
  async listResults(options: { search?: string; status?: string; testId?: string; skip?: number; limit?: number } = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)); });
    return request<Paginated<ResultRecord>>(`/results?${params.toString()}`);
  },
  async declareResult(objectId: string) {
    return request<{ objectId: string; status: string }>(`/results/${encodeURIComponent(objectId)}/declare`, { method: 'POST' });
  },
  async bulkResultAction(action: 'delete' | 'evaluate', resultIds: string[]) {
    return request<{ action: string; count: number }>('/results/bulk', { method: 'POST', body: JSON.stringify({ action, resultIds }) });
  },
  async listReportedQuestions(options: { search?: string; testId?: string; skip?: number; limit?: number } = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)); });
    return request<Paginated<ReportedQuestion>>(`/reported-questions?${params.toString()}`);
  },
  async listQuestionOfDay(options: { search?: string; fromDate?: string; toDate?: string; skip?: number; limit?: number } = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)); });
    return request<Paginated<QuestionOfDayRecord>>(`/question-of-day?${params.toString()}`);
  },
  async saveQuestionOfDay(data: { Date: string; QuestionPtr: string }) {
    return request<QuestionOfDayRecord>('/question-of-day', { method: 'POST', body: JSON.stringify(data) });
  },
  async deleteQuestionOfDay(objectId: string) {
    return request<{ objectId: string; deleted: boolean }>(`/question-of-day/${encodeURIComponent(objectId)}`, { method: 'DELETE' });
  },
  async createExport(report: string, options: { search?: string; status?: string } = {}) {
    return request<{ id: string; status: string; filename: string; count: number }>('/report-exports', { method: 'POST', body: JSON.stringify({ report, ...options }) });
  },
  async getExport(id: string) {
    return request<{ id: string; status: string; filename: string; content: string }>(`/report-exports/${encodeURIComponent(id)}`);
  },
  async getMapping(testId: string) {
    return request<{ testId: string; results: TestMapping[] }>(`/tests/${encodeURIComponent(testId)}/questions`);
  },
  async saveMapping(testId: string, entries: Array<{ questionId: string; indexNo: number; sectionName: string }>) {
    return request<{ testId: string; count: number; results: TestMapping[] }>(`/tests/${encodeURIComponent(testId)}/questions`, { method: 'POST', body: JSON.stringify({ entries }) });
  },
};

export function isUnauthorized(error: unknown) {
  return Boolean(error && typeof error === 'object' && 'status' in error && (error as { status?: number }).status === 401);
}
